import React, { useCallback, useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Trophy, Play, RefreshCw, AlertTriangle, Gauge, Zap, Clock } from 'lucide-react';
import benchmarkService, { BenchmarkRun, BenchmarkTarget } from '../../services/benchmarkService';
import { PageHeader, Spinner, EmptyState, InfoHint, Button } from '../../components/ui';
import { HELP } from '../../lib/metrics';
import '../../styles/theme.css';

// A column header with an inline help tooltip.
const HeadHelp: React.FC<{ label: string; text: string }> = ({ label, text }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{label}<InfoHint text={text} /></span>
);

const AXIS = { fontSize: 11, fill: 'var(--mx-text-subtle)' };
const tip = {
  background: 'var(--mx-surface)', border: '1px solid var(--mx-border)',
  borderRadius: 8, fontSize: 12, boxShadow: 'var(--mx-shadow-md)',
};
const pct = (v?: number) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`);

// Highlight the best cell in each accuracy column.
const bestOf = (rows: any[], key: string) => {
  const vals = rows.filter((r) => !r.error && r.docs > 0).map((r) => r[key] as number);
  return vals.length ? Math.max(...vals) : null;
};

const Benchmark: React.FC = () => {
  const [targets, setTargets] = useState<BenchmarkTarget[]>([]);
  const [latest, setLatest] = useState<BenchmarkRun | null>(null);
  const [history, setHistory] = useState<BenchmarkRun[]>([]);
  const [holdout, setHoldout] = useState(5);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const [t, r] = await Promise.all([benchmarkService.targets(), benchmarkService.runs()]);
    setTargets(t.targets);
    setHistory(r.runs);
    if (r.runs.length) setLatest(r.runs[0]);
  }, []);

  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  const run = async () => {
    setRunning(true); setMsg('Running each model over the golden holdout…');
    try {
      const { benchmark } = await benchmarkService.run(holdout);
      setLatest(benchmark);
      setMsg('');
      benchmarkService.runs().then((r) => setHistory(r.runs));
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Benchmark failed');
    } finally { setRunning(false); }
  };

  if (loading) return <div><PageHeader title="Benchmark" /><div className="mx-card mx-card-pad"><Spinner label="Loading…" /></div></div>;

  const rows = latest?.results || [];
  const chartData = rows.filter((r) => !r.error && r.docs > 0)
    .map((r) => ({ name: r.label.split(' (')[0], f1: r.f1, provider: r.provider }));
  const bestF1 = bestOf(rows, 'f1');
  const bestGr = bestOf(rows, 'grounded_rate');
  const bestCo = bestOf(rows, 'completeness');

  return (
    <div>
      <PageHeader title="Benchmark"
        subtitle="Every available model, scored head-to-head on the golden holdout with the same metrics the eval suite uses — plus latency, cost, and energy." />

      {msg && <div className="mx-card mx-card-pad" style={{ marginBottom: 16, fontSize: 13 }}>{msg}</div>}

      {/* Controls */}
      <div className="mx-card mx-card-pad" style={{ marginBottom: 16, display: 'flex',
        gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label className="mx-subtle" style={{ fontSize: 12, fontWeight: 600, display: 'block' }}>Holdout size</label>
          <select className="mx-select" value={holdout} onChange={(e) => setHoldout(Number(e.target.value))}>
            {[1, 3, 5, 10, 25].map((n) => <option key={n} value={n}>{n} docs</option>)}
          </select>
        </div>
        <Button icon={Play} variant="primary" onClick={run} disabled={running}>
          {running ? 'Benchmarking…' : 'Run benchmark'}</Button>
        <Button icon={RefreshCw} variant="ghost" size="sm" onClick={() => load()}>Refresh</Button>
        <div className="mx-subtle" style={{ fontSize: 12, marginLeft: 'auto' }}>
          Models under test: {targets.map((t) => t.label.split(' (')[0]).join(' · ')}
        </div>
      </div>

      {!latest ? (
        <div className="mx-card mx-card-pad"><EmptyState icon={<Trophy size={28} />} title="No benchmark yet"
          hint="Run a benchmark to compare every configured model on your golden-labelled documents." /></div>
      ) : (
        <>
          <div className="mx-grid" style={{ gridTemplateColumns: '1.2fr 1fr', marginBottom: 16 }}>
            {/* Leaderboard */}
            <div className="mx-card">
              <div className="mx-card-head">
                <h3 className="mx-card-title">Leaderboard</h3>
                <span className="mx-subtle" style={{ fontSize: 12 }}>{latest.holdout_size} golden doc(s)</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="mx-table">
                  <thead><tr>
                    <th>#</th><th>Method</th>
                    <th><HeadHelp label="Accuracy" text={HELP.accuracy} /></th>
                    <th><HeadHelp label="Correctness" text={HELP.correctness} /></th>
                    <th><HeadHelp label="Coverage" text={HELP.coverage} /></th>
                    <th><HeadHelp label="Complete" text={HELP.completeness} /></th>
                    <th><HeadHelp label="Verified" text={HELP.grounded} /></th>
                    <th>Speed</th><th>Cost</th><th>Energy</th>
                  </tr></thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const dead = !!r.error || r.docs === 0;
                      const hl = (v: number, best: number | null) =>
                        (!dead && best != null && v === best && best > 0
                          ? { color: 'var(--mx-success)', fontWeight: 700 } : {});
                      return (
                        <tr key={r.provider} style={dead ? { opacity: 0.55 } : {}}>
                          <td>{dead ? '—' : i + 1}
                            {i === 0 && !dead && <Trophy size={13} color="var(--mx-warning)" style={{ marginLeft: 4 }} />}</td>
                          <td style={{ fontWeight: 500 }}>{r.label}
                            <div className="mx-mono mx-subtle" style={{ fontSize: 10 }}>{r.model}</div>
                            {r.error && <div style={{ color: 'var(--mx-danger)', fontSize: 11, display: 'flex', gap: 4, alignItems: 'center' }}>
                              <AlertTriangle size={11} /> {r.error}</div>}</td>
                          {dead ? <td colSpan={8} className="mx-subtle" style={{ fontSize: 12 }}>did not score</td> : (
                            <>
                              <td style={hl(r.f1, bestF1)}>{pct(r.f1)}</td>
                              <td>{pct(r.precision)}</td>
                              <td>{pct(r.recall)}</td>
                              <td style={hl(r.completeness, bestCo)}>{pct(r.completeness)}</td>
                              <td style={hl(r.grounded_rate, bestGr)}>{pct(r.grounded_rate)}</td>
                              <td className="mx-subtle">{r.latency_ms ? `${Math.round(r.latency_ms)}ms` : '—'}</td>
                              <td className="mx-subtle">{r.cost_usd ? `$${r.cost_usd.toFixed(4)}` : '$0'}</td>
                              <td className="mx-subtle">{r.energy_joules ? `${Math.round(r.energy_joules)}J` : '—'}
                                {r.energy_source === 'estimated' && r.energy_joules ? '*' : ''}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mx-card-pad mx-subtle" style={{ fontSize: 11, paddingTop: 8 }}>
                Green = best in column. <b>*</b> energy estimated (hosted APIs); measured for local models.
                Latency/cost/energy are zero for the deterministic regex stream.
              </div>
            </div>

            {/* F1 chart */}
            <div className="mx-card">
              <div className="mx-card-head"><h3 className="mx-card-title">F1 by model</h3></div>
              <div className="mx-card-pad">
                {chartData.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={chartData} layout="vertical" margin={{ left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--mx-border)" horizontal={false} />
                      <XAxis type="number" domain={[0, 1]} tick={AXIS} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={AXIS} axisLine={false} tickLine={false} width={110} />
                      <Tooltip contentStyle={tip} formatter={(v: any) => pct(v as number)} cursor={{ fill: 'var(--mx-surface-2)' }} />
                      <Bar dataKey="f1" radius={[0, 4, 4, 0]}>
                        {chartData.map((d, i) => (
                          <Cell key={d.provider} fill={i === 0 ? 'var(--mx-c1)' : 'var(--mx-c2)'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="mx-muted" style={{ fontSize: 13 }}>No model scored on this run.</div>}
              </div>
            </div>
          </div>

          {/* How it works */}
          <div className="mx-card mx-card-pad" style={{ marginBottom: 16 }}>
            <h3 className="mx-card-title" style={{ marginBottom: 12 }}>How models are benchmarked</h3>
            <div className="mx-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              {[
                { icon: <Gauge size={16} />, t: 'Same yardstick as evals',
                  d: 'Each model runs the real extraction path over your golden-labelled docs and is scored with the identical metrics the eval suite uses — precision, recall, F1, completeness, grounding.' },
                { icon: <Trophy size={16} />, t: 'Head-to-head, same holdout',
                  d: 'Every model sees the same parsed documents, so differences are the model, not the input. Deterministic regex is the always-on baseline.' },
                { icon: <Clock size={16} />, t: 'Operational cost too',
                  d: 'Accuracy is not free — latency, dollar cost, and energy are measured per model so you can trade quality against speed and spend.' },
                { icon: <Zap size={16} />, t: 'Gates fine-tune promotion',
                  d: 'A fine-tuned model is promoted to the OSS stream only if it beats the incumbent on this exact benchmark — no silent regressions.' },
              ].map((c) => (
                <div key={c.t}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', color: 'var(--mx-accent)', fontWeight: 600, fontSize: 13 }}>
                    {c.icon} {c.t}</div>
                  <div className="mx-muted" style={{ fontSize: 12, marginTop: 4 }}>{c.d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* History */}
          {history.length > 1 && (
            <div className="mx-card">
              <div className="mx-card-head"><h3 className="mx-card-title">Past benchmarks</h3></div>
              <table className="mx-table mx-table-hover">
                <thead><tr><th>When</th><th>Docs</th><th>Models</th><th>Winner</th><th>Top F1</th></tr></thead>
                <tbody>
                  {history.map((h) => {
                    const top = [...h.results].filter((r) => !r.error && r.docs > 0).sort((a, b) => b.f1 - a.f1)[0];
                    return (
                      <tr key={h.id} onClick={() => setLatest(h)} style={{ cursor: 'pointer' }}>
                        <td>{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</td>
                        <td>{h.holdout_size}</td>
                        <td className="mx-subtle">{h.results.length}</td>
                        <td style={{ fontWeight: 500 }}>{h.winner || '—'}</td>
                        <td>{top ? pct(top.f1) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Benchmark;
